import { api } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AlertTriangle,
	Check,
	Clock,
	ExternalLink,
	LinkIcon,
	Loader2,
	Plus,
	Search,
	X,
} from "lucide-react";
import { useRef, useState } from "react";

// ============================================================
// Types
// ============================================================

export interface Voucher {
	id: string;
	voucherType: string;
	voucherStatus: string;
	voucherNumber: string | null;
	voucherDate: string | null;
	dueDate: string | null;
	contactName: string | null;
	totalAmount: number | null;
	openAmount: number | null;
	bookingId: string | null;
	lexofficeUrl?: string;
}

export interface OpenInvoice extends Voucher {
	isOverdue: boolean;
}

interface BookingSearchResult {
	id: string;
	kunde: string;
	firma: string | null;
	datum: string | null;
	status: string;
	art: string | null;
}

// ============================================================
// Unlinked Section
// ============================================================

export function UnlinkedSection({ vouchers }: { vouchers: Voucher[] }) {
	const [linkingId, setLinkingId] = useState<string | null>(null);

	if (vouchers.length === 0) {
		return (
			<div className="text-center py-12 text-stone-400">
				Alle Belege sind zugeordnet.
			</div>
		);
	}

	return (
		<div className="space-y-2">
			<p className="text-xs text-stone-500">
				{vouchers.length} Beleg{vouchers.length !== 1 ? "e" : ""} ohne
				Buchungs-Zuordnung
			</p>
			{vouchers.map((v) => (
				<VoucherCard
					key={v.id}
					voucher={v}
					showLink
					isLinking={linkingId === v.id}
					onStartLink={() => setLinkingId(v.id)}
					onCancelLink={() => setLinkingId(null)}
				/>
			))}
		</div>
	);
}

// ============================================================
// Invoices Section
// ============================================================

export function InvoicesSection({ invoices }: { invoices: OpenInvoice[] }) {
	if (invoices.length === 0) {
		return (
			<div className="text-center py-12 text-stone-400">
				Keine offenen Rechnungen.
			</div>
		);
	}

	return (
		<div className="card !p-0 overflow-hidden">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-stone-100 bg-stone-50/50 text-left">
						<th className="px-4 py-3 font-medium text-stone-500">Nummer</th>
						<th className="px-4 py-3 font-medium text-stone-500">Kunde</th>
						<th className="px-4 py-3 font-medium text-stone-500">Fällig</th>
						<th className="px-4 py-3 font-medium text-stone-500 text-right">
							Offen
						</th>
						<th className="px-4 py-3 font-medium text-stone-500">Status</th>
						<th className="px-4 py-3" />
					</tr>
				</thead>
				<tbody>
					{invoices.map((inv) => (
						<tr
							key={inv.id}
							className="border-b border-stone-50 hover:bg-stone-50/50"
						>
							<td className="px-4 py-3 font-mono text-xs">
								{inv.voucherNumber ?? "—"}
							</td>
							<td className="px-4 py-3">{inv.contactName ?? "—"}</td>
							<td className="px-4 py-3 text-stone-600">
								{formatDate(inv.dueDate)}
							</td>
							<td className="px-4 py-3 text-right font-mono text-xs">
								{formatCurrency(inv.openAmount)}
							</td>
							<td className="px-4 py-3">
								{inv.isOverdue ? (
									<span className="badge bg-red-100 text-red-700 inline-flex items-center gap-1">
										<AlertTriangle size={10} /> Überfällig
									</span>
								) : (
									<span className="badge bg-amber-100 text-amber-700 inline-flex items-center gap-1">
										<Clock size={10} /> Offen
									</span>
								)}
							</td>
							<td className="px-4 py-3">
								{inv.lexofficeUrl && (
									<a
										href={inv.lexofficeUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="text-stone-400 hover:text-brand-600 transition-colors"
										title="In LexOffice öffnen"
									>
										<ExternalLink size={14} />
									</a>
								)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

// ============================================================
// Voucher Card (Unlinked mit Inline-Linking + Buchung erstellen)
// ============================================================

function VoucherCard({
	voucher,
	showLink,
	isLinking,
	onStartLink,
	onCancelLink,
}: {
	voucher: Voucher;
	showLink?: boolean;
	isLinking: boolean;
	onStartLink: () => void;
	onCancelLink: () => void;
}) {
	const queryClient = useQueryClient();

	const linkMutation = useMutation({
		mutationFn: (bookingId: string) =>
			api.post(`/lexoffice/vouchers/${voucher.id}/link`, { bookingId }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["lexoffice"] });
			onCancelLink();
		},
	});

	const createMutation = useMutation({
		mutationFn: () =>
			api.post(`/lexoffice/vouchers/${voucher.id}/create-booking`),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["lexoffice"] });
			queryClient.invalidateQueries({ queryKey: ["bookings"] });
		},
	});

	return (
		<div className="card !p-0 overflow-hidden">
			<div className="flex items-center gap-3 px-4 py-3">
				<VoucherTypeBadge type={voucher.voucherType} />
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium text-stone-700">
							{voucher.voucherNumber ?? "Entwurf"}
						</span>
						<span className="text-xs text-stone-500">
							{voucher.contactName ?? "Kein Kontakt"}
						</span>
					</div>
					<div className="text-xs text-stone-400 mt-0.5">
						{formatDate(voucher.voucherDate)} ·{" "}
						{formatCurrency(voucher.totalAmount)}
					</div>
				</div>
				<div className="flex items-center gap-1.5 shrink-0">
					{voucher.lexofficeUrl && (
						<a
							href={voucher.lexofficeUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="p-1.5 text-stone-400 hover:text-brand-600 rounded transition-colors"
							title="In LexOffice öffnen"
						>
							<ExternalLink size={14} />
						</a>
					)}
					{showLink && !isLinking && (
						<>
							<button
								type="button"
								onClick={() => createMutation.mutate()}
								disabled={createMutation.isPending}
								className="btn-secondary !py-1 !px-2 text-xs"
								title="Buchung aus Beleg erstellen"
							>
								{createMutation.isPending ? (
									<Loader2 size={12} className="animate-spin" />
								) : (
									<Plus size={12} />
								)}
								Buchung
							</button>
							<button
								type="button"
								onClick={onStartLink}
								className="btn-secondary !py-1 !px-2 text-xs"
							>
								<LinkIcon size={12} />
								Zuordnen
							</button>
						</>
					)}
					{isLinking && (
						<button
							type="button"
							onClick={onCancelLink}
							className="p-1.5 text-stone-400 hover:text-stone-600 rounded transition-colors"
						>
							<X size={14} />
						</button>
					)}
				</div>
			</div>

			{/* Inline-Suche */}
			{isLinking && (
				<div className="border-t border-stone-100 bg-stone-50 px-4 py-3">
					<BookingSearchPicker
						onSelect={(bookingId) => linkMutation.mutate(bookingId)}
						isLinking={linkMutation.isPending}
					/>
				</div>
			)}

			{/* Erstellt-Meldung */}
			{createMutation.isSuccess && (
				<div className="border-t border-green-100 bg-green-50 px-4 py-2 text-xs text-green-700 flex items-center gap-1.5">
					<Check size={12} />
					Buchung erstellt
				</div>
			)}
		</div>
	);
}

// ============================================================
// Booking Search Picker
// ============================================================

function BookingSearchPicker({
	onSelect,
	isLinking,
}: {
	onSelect: (bookingId: string) => void;
	isLinking: boolean;
}) {
	const [search, setSearch] = useState("");
	const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	const searchQuery = useQuery({
		queryKey: ["lexoffice", "booking-search", search],
		queryFn: () =>
			api.get<{ data: BookingSearchResult[] }>(
				`/lexoffice/booking-search?q=${encodeURIComponent(search)}`,
			),
		enabled: search.length >= 2,
	});

	const handleInput = (value: string) => {
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => setSearch(value), 300);
	};

	const results = searchQuery.data?.data ?? [];

	return (
		<div className="space-y-2">
			<div className="relative">
				<Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" />
				<input
					type="text"
					onChange={(e) => handleInput(e.target.value)}
					placeholder="Buchung suchen (Name, Firma, E-Mail)..."
					className="input !py-1.5 pl-8 text-xs"
				/>
			</div>

			{searchQuery.isLoading && search.length >= 2 && (
				<div className="flex justify-center py-2">
					<Loader2 className="h-4 w-4 animate-spin text-stone-400" />
				</div>
			)}

			{results.length > 0 && (
				<div className="space-y-1 max-h-48 overflow-y-auto">
					{results.map((b) => (
						<button
							key={b.id}
							type="button"
							onClick={() => onSelect(b.id)}
							disabled={isLinking}
							className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-md hover:bg-white transition-colors text-xs"
						>
							<div className="flex-1 min-w-0">
								<span className="font-medium text-stone-700">{b.kunde}</span>
								{b.firma && (
									<span className="text-stone-500 ml-1">({b.firma})</span>
								)}
								<div className="text-stone-400 mt-0.5">
									{b.datum ? formatDate(b.datum) : "—"} · {b.status}
									{b.art && ` · ${b.art}`}
								</div>
							</div>
							{isLinking ? (
								<Loader2 className="h-3 w-3 animate-spin text-stone-400" />
							) : (
								<Check size={14} className="text-green-600 shrink-0" />
							)}
						</button>
					))}
				</div>
			)}

			{search.length >= 2 && !searchQuery.isLoading && results.length === 0 && (
				<p className="text-xs text-stone-400 text-center py-2">
					Keine Buchungen gefunden.
				</p>
			)}
		</div>
	);
}

// ============================================================
// Helpers
// ============================================================

export function VoucherTypeBadge({ type }: { type: string }) {
	const config: Record<string, { label: string; color: string }> = {
		invoice: { label: "Rechnung", color: "bg-orange-100 text-orange-700" },
		quotation: { label: "Angebot", color: "bg-blue-100 text-blue-700" },
		orderconfirmation: {
			label: "AB",
			color: "bg-emerald-100 text-emerald-700",
		},
		creditnote: {
			label: "Gutschrift",
			color: "bg-purple-100 text-purple-700",
		},
	};
	const c = config[type] ?? {
		label: type,
		color: "bg-stone-100 text-stone-600",
	};
	return <span className={`badge ${c.color}`}>{c.label}</span>;
}
